/**
 * Storage (/storage, /storage/:folderId, /storage?view=memes): folder tree + file browser with upload, filter, view toggle,
 * preview and bulk move/delete. The meme collection spans folders and is searched on the server.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FolderInput, FolderPlus, Laugh, LayoutGrid, List, Search, Trash2, Upload, X } from 'lucide-react'
import { useT } from '../i18n'
import { cn } from '../lib/utils'
import { useDebounce } from '../hooks/use-debounce'
import { useMemeFiles, useStorageFiles, useStorageFolderTree, useUploadToFolder, useBulkDelete, useBulkMove, type StorageFile } from '../hooks/use-storage'
import { StorageSidebar } from '../components/storage/storage-sidebar'
import { StorageBreadcrumb } from '../components/storage/storage-breadcrumb'
import { StorageFileGrid } from '../components/storage/storage-file-grid'
import { StorageDropZone } from '../components/storage/storage-drop-zone'
import { StoragePreviewModal } from '../components/storage/storage-preview-modal'
import { UploadProgressList } from '../components/storage/upload-progress-list'
import { StorageUploadDialog, type DescribedFile } from '../components/storage/storage-upload-dialog'
import { ConfirmDeleteDialog, CreateFolderDialog, MoveItemsDialog } from '../components/storage/storage-dialogs'
import { getSubfolders, indexFolders, selectAllState, toggleId } from '../components/storage/storage-tree-utils'
import { buttonDangerClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, inputClass } from '../components/ui/styles'

type ViewMode = 'grid' | 'list'

interface Selection {
  files: Set<string>
  folders: Set<string>
}

const emptySelection = (): Selection => ({ files: new Set(), folders: new Set() })
const errorMessage = (err: unknown) => (err instanceof Error ? err.message : String(err))

export function StoragePage() {
  const t = useT()
  const navigate = useNavigate()
  const { folderId: routeFolderId } = useParams<{ folderId?: string }>()
  const [searchParams] = useSearchParams()
  const memesView = !routeFolderId && searchParams.get('view') === 'memes'
  const activeFolderId = routeFolderId ?? null

  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid'))
  const [selection, setSelection] = useState<Selection>(emptySelection)
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null)
  const [pendingUpload, setPendingUpload] = useState<{ files: File[]; folderId: string | null } | null>(null)
  const [createFolderOpen, setCreateFolderOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<StorageFile | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectAllRef = useRef<HTMLInputElement>(null)

  // A different folder (or the meme collection) starts with a clean filter and selection
  const viewKey = memesView ? 'memes' : activeFolderId
  const [shownView, setShownView] = useState(viewKey)
  if (shownView !== viewKey) {
    setShownView(viewKey)
    setSearch('')
    setSelection(emptySelection())
  }

  const tree = useStorageFolderTree()
  const folderFiles = useStorageFiles(activeFolderId, !memesView)
  // Memes are searched on the server by filename and description
  const memeSearch = useDebounce(search, 250)
  const memeFiles = useMemeFiles(memeSearch, memesView)
  const files = memesView ? memeFiles : folderFiles
  const uploadToFolder = useUploadToFolder()
  const bulkMove = useBulkMove()
  const bulkDelete = useBulkDelete()

  const folders = useMemo(() => tree.data?.folders ?? [], [tree.data])
  const folderIndex = useMemo(() => indexFolders(folders), [folders])
  const activeFolder = activeFolderId ? folderIndex.get(activeFolderId) : undefined
  const folderMissing = !!activeFolderId && tree.isSuccess && !activeFolder
  const folderName = (id: string | null) => (id ? folderIndex.get(id)?.name ?? t('storage.root') : t('storage.root'))
  const uploadTargetName = (id: string | null) => (memesView ? t('storage.memes') : folderName(id))

  const allFiles = useMemo(() => files.data?.pages.flatMap((p) => p.files) ?? [], [files.data])
  const query = search.trim().toLowerCase()
  const visibleFiles = useMemo(
    () => (query && !memesView ? allFiles.filter((f) => f.filename.toLowerCase().includes(query)) : allFiles),
    [allFiles, query, memesView],
  )
  const visibleFolders = useMemo(() => {
    if (memesView) return []
    const subs = getSubfolders(folders, activeFolderId)
    return query ? subs.filter((f) => f.name.toLowerCase().includes(query)) : subs
  }, [folders, activeFolderId, query, memesView])

  // Only items still on screen count as selected (filtering or a refetch can hide some)
  const selectedFileIds = useMemo(() => visibleFiles.filter((f) => selection.files.has(f.id)).map((f) => f.id), [visibleFiles, selection.files])
  const selectedFolderIds = useMemo(() => visibleFolders.filter((f) => selection.folders.has(f.id)).map((f) => f.id), [visibleFolders, selection.folders])
  const selectedCount = selectedFileIds.length + selectedFolderIds.length
  const visibleCount = visibleFiles.length + visibleFolders.length
  const allState = selectAllState(selectedCount, visibleCount)

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = allState === 'some'
  }, [allState])

  const selectFolder = useCallback((id: string | null) => navigate(id ? `/storage/${id}` : '/storage'), [navigate])
  const selectMemes = useCallback(() => navigate('/storage?view=memes'), [navigate])

  const toggleFile = useCallback((id: string) => setSelection((s) => ({ ...s, files: toggleId(s.files, id) })), [])
  const toggleFolder = useCallback((id: string) => setSelection((s) => ({ ...s, folders: toggleId(s.folders, id) })), [])
  const toggleAll = () =>
    setSelection(
      allState === 'all' ? emptySelection() : { files: new Set(visibleFiles.map((f) => f.id)), folders: new Set(visibleFolders.map((f) => f.id)) },
    )

  const openUpload = useCallback((picked: File[]) => {
    if (picked.length) setPendingUpload({ files: picked, folderId: activeFolderId })
  }, [activeFolderId])

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    openUpload(Array.from(e.target.files ?? []))
    // Allow picking the same file again later
    e.target.value = ''
  }

  const startUpload = async (items: DescribedFile[], folderId: string | null) => {
    setPendingUpload(null)
    const collection = memesView ? 'meme' : undefined
    const results = await Promise.allSettled(items.map((i) => uploadToFolder(i.file, memesView ? null : folderId, i.description, collection)))
    const stored = results.filter((r) => r.status === 'fulfilled' && r.value).length
    if (stored > 0) toast.success(t('storage.upload.done', { count: stored }))
    if (stored < items.length) toast.error(t('storage.upload.someFailed', { count: items.length - stored }))
  }

  const confirmFileDelete = async () => {
    if (!deleteTarget) return
    try {
      await bulkDelete.mutateAsync({ fileIds: [deleteTarget.id], folderIds: [] })
      toast.success(t('storage.file.deleted', { name: deleteTarget.filename }))
      setDeleteTarget(null)
    } catch (err) {
      toast.error(t('storage.error.generic', { message: errorMessage(err) }))
    }
  }

  const confirmBulkDelete = async () => {
    try {
      await bulkDelete.mutateAsync({ fileIds: selectedFileIds, folderIds: selectedFolderIds })
      toast.success(t('storage.bulk.deleted', { count: selectedCount }))
      setBulkDeleteOpen(false)
      setSelection(emptySelection())
    } catch (err) {
      toast.error(t('storage.error.generic', { message: errorMessage(err) }))
    }
  }

  const moveSelected = async (targetFolderId: string | null) => {
    try {
      await bulkMove.mutateAsync({ fileIds: selectedFileIds, folderIds: selectedFolderIds, targetFolderId })
      toast.success(t('storage.bulk.moved', { count: selectedCount, name: folderName(targetFolderId) }))
      setMoveOpen(false)
      setSelection(emptySelection())
    } catch (err) {
      toast.error(t('storage.error.generic', { message: errorMessage(err) }))
    }
  }

  const dialogOpen = !!pendingUpload || createFolderOpen || moveOpen || bulkDeleteOpen || !!deleteTarget || !!previewFile

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{memesView ? t('storage.memes') : t('storage.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{memesView ? t('storage.memes.subtitle') : t('storage.subtitle')}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => (memesView ? selectFolder(null) : selectMemes())}
            aria-pressed={memesView}
            className={cn(buttonSecondaryClass, 'md:hidden')}
          >
            <Laugh className="h-4 w-4" aria-hidden="true" />
            {t('storage.memes')}
          </button>
          <button type="button" onClick={() => setCreateFolderOpen(true)} disabled={folderMissing || memesView} className={buttonSecondaryClass}>
            <FolderPlus className="h-4 w-4" aria-hidden="true" />
            {t('storage.folder.new')}
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={folderMissing} className={buttonPrimaryClass}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('storage.upload.button')}
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={handlePick} className="hidden" aria-label={t('storage.upload.pick')} tabIndex={-1} />
        </div>
      </header>

      <div className="flex gap-6">
        <aside className="hidden w-60 shrink-0 md:block" aria-label={t('storage.folders')}>
          <div className="sticky top-0">
            <StorageSidebar activeFolderId={activeFolderId} onSelect={selectFolder} memesActive={memesView} onSelectMemes={selectMemes} />
          </div>
        </aside>

        <section className="min-w-0 flex-1 space-y-4" aria-label={t('storage.browser')}>
          {!memesView && <StorageBreadcrumb folders={folders} activeFolderId={activeFolderId} onSelect={selectFolder} />}

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-muted">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allState === 'all'}
                onChange={toggleAll}
                disabled={visibleCount === 0}
                className="h-4 w-4 cursor-pointer rounded accent-accent disabled:cursor-not-allowed"
              />
              {t('storage.select.all')}
            </label>
            <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={memesView ? t('storage.memes.search.placeholder') : t('storage.filter.placeholder')}
                aria-label={memesView ? t('storage.memes.search.label') : t('storage.filter.label')}
                className={cn(inputClass, 'pl-8')}
              />
            </div>
            <div className="ml-auto flex rounded-md border border-border" role="group" aria-label={t('storage.view.label')}>
              {(['grid', 'list'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  aria-pressed={viewMode === mode}
                  aria-label={mode === 'grid' ? t('storage.view.grid') : t('storage.view.list')}
                  title={mode === 'grid' ? t('storage.view.grid') : t('storage.view.list')}
                  className={cn(
                    'inline-flex h-9 w-9 cursor-pointer items-center justify-center first:rounded-l-md last:rounded-r-md',
                    viewMode === mode ? 'bg-surface-hover text-fg' : 'text-fg-subtle hover:bg-surface-hover hover:text-fg',
                  )}
                >
                  {mode === 'grid' ? <LayoutGrid className="h-4 w-4" aria-hidden="true" /> : <List className="h-4 w-4" aria-hidden="true" />}
                </button>
              ))}
            </div>
          </div>

          {selectedCount > 0 && (
            <div role="toolbar" aria-label={t('storage.bulk.label')} className="flex flex-wrap items-center gap-2 rounded-lg border border-accent bg-accent-soft px-3 py-2">
              <span className="mr-auto text-sm font-medium text-fg" aria-live="polite">
                {t('storage.bulk.selected', { count: selectedCount })}
              </span>
              <button type="button" onClick={() => setMoveOpen(true)} className={buttonSecondaryClass}>
                <FolderInput className="h-4 w-4" aria-hidden="true" />
                {t('storage.bulk.move')}
              </button>
              <button type="button" onClick={() => setBulkDeleteOpen(true)} className={buttonDangerClass}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t('storage.bulk.delete')}
              </button>
              <button type="button" onClick={() => setSelection(emptySelection())} className={buttonSecondaryClass}>
                <X className="h-4 w-4" aria-hidden="true" />
                {t('storage.bulk.clear')}
              </button>
            </div>
          )}

          <UploadProgressList className="" />

          {tree.isError && (
            <div className={errorTextClass} role="alert">
              {t('storage.error.folders')}{' '}
              <button type="button" onClick={() => void tree.refetch()} className="cursor-pointer font-medium underline underline-offset-2">
                {t('common.retry')}
              </button>
            </div>
          )}

          {folderMissing ? (
            <div className={errorTextClass} role="alert">
              {t('storage.error.folderMissing')}{' '}
              <button type="button" onClick={() => selectFolder(null)} className="cursor-pointer font-medium underline underline-offset-2">
                {t('storage.backToRoot')}
              </button>
            </div>
          ) : files.isError ? (
            <div className={errorTextClass} role="alert">
              {t('storage.error.files')}{' '}
              <button type="button" onClick={() => void files.refetch()} className="cursor-pointer font-medium underline underline-offset-2">
                {t('common.retry')}
              </button>
            </div>
          ) : (
            <>
              <StorageFileGrid
                subfolders={visibleFolders}
                files={visibleFiles}
                viewMode={viewMode}
                selectedFileIds={selection.files}
                selectedFolderIds={selection.folders}
                onToggleFile={toggleFile}
                onToggleFolder={toggleFolder}
                onFolderOpen={selectFolder}
                onFileDelete={setDeleteTarget}
                onFilePreview={setPreviewFile}
                isLoading={files.isLoading}
                filtered={!!query}
                emptyMessages={memesView
                  ? { empty: t('storage.memes.empty'), hint: t('storage.memes.emptyHint'), filtered: t('storage.memes.noMatch') }
                  : undefined}
              />
              {files.hasNextPage && (
                <div className="flex justify-center pt-2">
                  <button type="button" onClick={() => void files.fetchNextPage()} disabled={files.isFetchingNextPage} className={buttonSecondaryClass}>
                    {files.isFetchingNextPage ? t('common.loading') : t('storage.loadMore')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {pendingUpload && (
        <StorageUploadDialog
          files={pendingUpload.files}
          folderName={uploadTargetName(pendingUpload.folderId)}
          onClose={() => setPendingUpload(null)}
          onSubmit={(items) => void startUpload(items, pendingUpload.folderId)}
        />
      )}

      {createFolderOpen && (
        <CreateFolderDialog parentId={activeFolderId} parentName={folderName(activeFolderId)} onClose={() => setCreateFolderOpen(false)} />
      )}

      {moveOpen && (
        <MoveItemsDialog
          tree={folders}
          currentFolderId={activeFolderId}
          folderIds={selectedFolderIds}
          itemCount={selectedCount}
          pending={bulkMove.isPending}
          onClose={() => setMoveOpen(false)}
          onMove={(target) => void moveSelected(target)}
        />
      )}

      {bulkDeleteOpen && (
        <ConfirmDeleteDialog
          title={t('storage.bulk.deleteTitle', { count: selectedCount })}
          description={selectedFolderIds.length > 0 ? t('storage.bulk.deleteWithFolders') : t('storage.bulk.deleteFiles')}
          confirmLabel={t('storage.bulk.delete')}
          pending={bulkDelete.isPending}
          onClose={() => setBulkDeleteOpen(false)}
          onConfirm={() => void confirmBulkDelete()}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDialog
          title={t('storage.file.deleteTitle')}
          description={t('storage.file.deleteConfirm', { name: deleteTarget.filename })}
          confirmLabel={t('storage.file.delete')}
          pending={bulkDelete.isPending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void confirmFileDelete()}
        />
      )}

      <StoragePreviewModal file={previewFile} files={visibleFiles} onClose={() => setPreviewFile(null)} onNavigate={setPreviewFile} />

      <StorageDropZone folderName={uploadTargetName(activeFolderId)} disabled={dialogOpen || folderMissing} onDropFiles={openUpload} />
    </div>
  )
}
